import { UI_VERSION, DIRECT_DOMAIN, MSP_ENABLED, FALL_BACK_VERSION_ENABLED } from './version';
import Registry from '@reporting-n-viz/oneapp-extensibility';

const manage_app = ({
	sparkyFramework: {
		events,
		onAuthed,
		StartupManager,
		getFrameworkServiceUrl,
		dedupPromise,
		fawkesUtils: { getRedirectURL, getAuthHeaders },
		getAuthState,
		getMainState,
		hasTsgSupport,
	},
}) => {
	onAuthed(
		({
			auth,
			getFeatures,
			getLicenseInfo,
			getFqdnInfo,
			getAuthInfo,
			getSubTenantInfo,
			getMigrationStatus,
			getNotifications,
			setMainValues,
			getMainState,
			setValues,
			waitExtraPromises,
		}) => {
			if (
				auth?.tsg_id &&
				auth.instances?.tsg_id &&
				!auth.instances.error &&
				!auth.instances.isFawkesRunning &&
				!auth.instances.isRunningStatus('bulk_config')
			) {
				return; // skip api calls if no running fawkes/bulk config instance
			}

			// TODO: move user api from sparky to fn
			getFeatures();
			getLicenseInfo();
			getFqdnInfo();
			getAuthInfo();
			getMigrationStatus(); // TODO: move to fawkes side in fn after version check
			getNotifications(); // TODO: remove if no longer needed, or move to fn
			const state = getMainState();
			const type = state.tenant.type;
			const getWebSecurityPromise = () => {
				const url = new URL('/api/config/v9.2/enable', getRedirectURL()).toString();
				return dedupPromise(
					`${url}?tsgId=${auth.tsg_id}`,
					() => {
						const controller = new AbortController();
						const timeoutId = setTimeout(() => controller.abort(), 10000);
						return fetch(url, {
							signal: controller.signal,
							withCredentials: true,
							method: 'GET',
							responseType: 'json',
							headers: getAuthHeaders(),
						})
							.then(async (response) => {
								let resp = await response.json();
								const isEnable = resp?.result?.enable;
								if (isEnable === 'yes') setValues({ webSecurityAvail: true });
								clearTimeout(timeoutId);
							})
							.catch((err) => {
								console.log(err);
							});
					},
					{ keepPromiseIn: 10000 },
				);
			};

			const getGpEPPromise = () => {
				const url = new URL(
					'/api/config/v9.2/configByPath?type=container&folder=Mobile%20Users%20Container&prefix=plugins/cloud_services&subPath=enable',
					getRedirectURL(),
				).toString();
				return dedupPromise(
					`${url}?tsgId=${auth.tsg_id}`,
					() => {
						const controller = new AbortController();
						const timeoutId = setTimeout(() => controller.abort(), 10000);
						return fetch(url, {
							signal: controller.signal,
							withCredentials: true,
							method: 'GET',
							responseType: 'json',
							headers: getAuthHeaders(),
						})
							.then(async (response) => {
								let resp = await response.json();
								const gpEpEnabled = resp?.result?.enable;
								if (gpEpEnabled) {
									setValues({ gpEpEnabled });
								}
								clearTimeout(timeoutId);
							})
							.catch((err) => {
								console.log(err);
							});
					},
					{ keepPromiseIn: 10000 },
				);
			};

			const DASLicenseFetching = () => {
				const url = new URL(
					'/subscription/v1/refresh_tenant_device_licenses',
					getFrameworkServiceUrl('api_gateway'),
				).toString();
				return dedupPromise(
					`${url}?tsgId=${auth.tsg_id}`,
					() => {
						const header = getAuthHeaders();
						const newHeader = {
							Authorization: `Bearer ${header['x-auth-jwt']}`,
						};
						return fetch(url, {
							withCredentials: true,
							method: 'GET',
							headers: newHeader,
						})
							.then((response) => {
								return response.text();
							})
							.catch((err) => {
								console.log(err);
							});
					},
					{ keepPromiseIn: 10000 },
				);
			};
			DASLicenseFetching(); // TODO: fn after version check

			if (StartupManager.getConfigManagedType() === StartupManager.PANORAMA_CONFIG) {
				getSubTenantInfo();
			}
			if (type !== StartupManager.PANORAMA_CONFIG)
				// TODO: getWebSecurityPromise and getGpEPPromise should move to fn after version check
				waitExtraPromises([getWebSecurityPromise(), getGpEPPromise()]);
			//checking if sparky info is loaded and setting configLocation if not available
			//upon landing on SaaS security screen, configLocation is not yet set.
			// let intervalRuns = 0;
			// let interval = setInterval(()=>{
			//     const mainState = getMainState() || {};
			//     const {
			//         infoLoaded,
			//         configLocation = {},
			//         featuresInfo = [],
			//         tenant: {
			//             'ngfw_enabled': isNGFWEnabled = false
			//         } = {}
			//     } = mainState;

			//     if(infoLoaded && !configLocation?.container){

			//         const defaultContainer = !isNGFWEnabled ? {
			//             name: PRISMA_ACCESS_CONTAINER,
			//             type: 'container',
			//             locationPath: PRISMA_ACCESS_CONTAINER
			//         } : {
			//             name: NGFW_SHARED,
			//             type: 'container',
			//             locationPath: NGFW_SHARED
			//         };
			//         setMainValues({
			//             configLocation: {
			//                 container: defaultContainer
			//             },
			//         });

			//         // clearInterval(interval)
			//     }else{
			//         intervalRuns++;
			//         if(intervalRuns > 10) clearInterval(interval);
			//     }
			// },500)
		},
	);
	//local version set during deploy time
	let localVersion = UI_VERSION;
	let fn = ({ auth, onTsgUnload }) => {
		//Entering the TSG
		let adminURL = getAuthState()?.instances?.get('prisma_access')?.runtime_attributes?.admin_api_url;
		fetch(adminURL + `/api/activity/login`, {
			withCredentials: true,
			method: 'GET',
			responseType: 'json',
			headers: getAuthHeaders(),
		})
			.then(async (response) => {
				console.log('Entering the TSG ID: ', auth.tsg_id, 'TSG Name: ', auth.tsg_name);
			})
			.catch((err) => {
				console.log(err);
			});

		events?.once?.('logout', () => {
			const url = new URL(`/api/activity/logout`, adminURL).toString();
			return dedupPromise(
				`${url}?tsg_id=${auth.tsg_id}`,
				() =>
					fetch(url, {
						withCredentials: true,
						method: 'GET',
						responseType: 'json',
						headers: getAuthHeaders(),
					})
						.then(async (response) => {
							console.log('Leaving the TSG ID: ', auth.tsg_id, 'TSG Name: ', auth.tsg_name);
						})
						.catch((err) => {
							console.log(err);
						}),
				{ keepPromiseIn: 10000 },
			);
		});

		// const isXavierFeatureEnabled = (featureList = [], key) => {
		//     for(let i=0;i<featureList.length;i++){
		//         if(featureList[i].name === key && featureList[i].toggle === 1){
		//           return true;
		//         }
		//     }
		//     return false;
		// }
		// const featureList = getMainState()?.featuresInfo || [];
		// if(isXavierFeatureEnabled(featureList, 'xavier-schema')) {
		//     // load xavier schema in case feature is enabled
		//     console.log('XavierFeatureEnabled');
		//     return Promise.all([
		//         import(/* webpackPreload: true, webpackChunkName: "predef" */ './xavier'),
		//         import(/* webpackPreload: true */ './i18n'),
		//     ]).then(() => import(/* webpackPreload: true */ './routes')).then(r => r.default)
		// } else

		// load regular schema
		return Promise.all([
			import(/* webpackPreload: true, webpackChunkName: "predef" */ './predefined'),
			import(/* webpackPreload: true */ './i18n'),
		])
			.then(() => import(/* webpackPreload: true */ './routes'))
			.then((r) => r.default);
	};
	// return (params) => fn(params)
	const contributeExtension = () => {
		const extensionKey = 'aiops.posture_settings';
		const path = '/security-checks-cm';
		const subKey = `${path}-active`;
		const extensionId = 'aiops-ngfw-security-checks-cm-active';

		const postureSettingsExtensions = Registry.getExtensions(extensionKey) || [];
		const existingExtension = postureSettingsExtensions.find((ext) => ext.key === subKey);
		if (existingExtension) {
			return;
		}
		try {
			Registry.contributeExtension(extensionKey, {
				id: extensionId,
				config: {
					key: subKey,
					path,
					name: {
						defaultMessage: 'Security Checks',
					},
					component: () => import('./containers/securityChecks/index'),
					position: ['start'],
				},
			});
		} catch (error) {
			console.error('Failed to contribute extension', error);
		}
	};

	//Need to handle MSP case
	if (hasTsgSupport() && !DIRECT_DOMAIN && !MSP_ENABLED) {
		return (params) => {
			if (!getAuthState()?.instances?.isFawkesRunning) {
				// no running fawkes
				if (FALL_BACK_VERSION_ENABLED) {
					contributeExtension();
				}
				return null;
			}
			//detect the version
			let version = getAuthState()?.instances?.get('prisma_access')?.runtime_attributes?.version;
			console.log('version check - Sparky Version: ', version, ' - LocalVersion:', localVersion);
			if (localVersion === version) {
				console.log('Loading Version: ', localVersion);
				contributeExtension();
				//varsion match. Load this site
				return fn(params);
			} else {
				//return null
				return null;
			}
		};
	} else {
		if (MSP_ENABLED) {
			//MSP tenant
			//detect the version
			return (params) => {
				let version = getAuthState()?.instances?.get('bulk_config')?.runtime_attributes?.version;
				console.log('MSP version check - Sparky Version: ', version, ' - LocalVersion:', localVersion);
				if (localVersion === version) {
					console.log('MSP Loading Version: ', localVersion);
					//varsion match. Load this site
					return fn(params);
				} else {
					//return null
					return null;
				}
			};
		} else {
			//Non TSG
			console.log('Loading Cloud Management for NonTsg or Direct Domain');
			return (params) => fn(params);
		}
	}
};
// manage_app.exposes = {
//     default: {
//         responsePage: () => import('./exposedFunctions.js'),
//     }
// }
export default manage_app;
